import { useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Printer, Download } from "lucide-react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const PreorderTemplate = () => {
  const printRef = useRef();

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadPDF = () => {
    const doc = new jsPDF();
    
    // Title
    doc.setFontSize(24);
    doc.setTextColor(44, 24, 16);
    doc.text("The Three Horseshoes", 105, 20, { align: "center" });
    
    // Subtitle
    doc.setFontSize(14);
    doc.setTextColor(193, 124, 58);
    doc.text("Food Preorder Form", 105, 28, { align: "center" });
    
    // Booking Info Fields
    doc.setFontSize(11);
    doc.setTextColor(44, 24, 16);
    
    const fieldY = 40;
    const fieldSpacing = 12;
    
    doc.setFont(undefined, "bold");
    doc.text("Booking Name:", 20, fieldY);
    doc.setFont(undefined, "normal");
    doc.line(55, fieldY, 190, fieldY);
    
    doc.setFont(undefined, "bold");
    doc.text("Contact Number:", 20, fieldY + fieldSpacing);
    doc.setFont(undefined, "normal");
    doc.line(60, fieldY + fieldSpacing, 190, fieldY + fieldSpacing);
    
    doc.setFont(undefined, "bold");
    doc.text("Date:", 20, fieldY + fieldSpacing * 2);
    doc.setFont(undefined, "normal");
    doc.line(35, fieldY + fieldSpacing * 2, 100, fieldY + fieldSpacing * 2);
    
    doc.setFont(undefined, "bold");
    doc.text("Time:", 110, fieldY + fieldSpacing * 2);
    doc.setFont(undefined, "normal");
    doc.line(125, fieldY + fieldSpacing * 2, 190, fieldY + fieldSpacing * 2);
    
    // Table
    const tableData = Array.from({ length: 20 }, (_, i) => [
      (i + 1).toString(),
      "",
      "",
      "",
      "",
      "",
      ""
    ]);
    
    autoTable(doc, {
      startY: 75,
      head: [["#", "Guest Name", "Starter", "Main", "Dessert", "Sides", "Special Req."]],
      body: tableData,
      theme: "grid",
      headStyles: {
        fillColor: [44, 24, 16],
        textColor: [255, 255, 255],
        fontStyle: "bold",
        fontSize: 9
      },
      bodyStyles: {
        fontSize: 9,
        cellPadding: 3,
        minCellHeight: 10
      },
      columnStyles: {
        0: { cellWidth: 10, halign: "center" },
        1: { cellWidth: 30 },
        2: { cellWidth: 28 },
        3: { cellWidth: 28 },
        4: { cellWidth: 28 },
        5: { cellWidth: 25 },
        6: { cellWidth: 35 }
      },
      margin: { left: 10, right: 10 }
    });
    
    // Footer
    const finalY = doc.lastAutoTable.finalY + 10;
    doc.setFontSize(9);
    doc.setTextColor(100, 100, 100);
    doc.text("Please email this completed form to The Three Horseshoes at least 48 hours before your booking.", 105, finalY, { align: "center" });
    doc.text("To book a table, please call us directly.", 105, finalY + 5, { align: "center" });
    doc.text("High St, Pentwyn-mawr, Newbridge, Newport NP11 4HN", 105, finalY + 10, { align: "center" });
    
    // Save
    doc.save("ThreeHorseshoes_Preorder_Form.pdf");
  };

  // Generate 20 rows for the template
  const rows = Array.from({ length: 20 }, (_, i) => i + 1);

  return (
    <div className="min-h-screen bg-[#F5F1E6]">
      {/* Header - Hidden when printing */}
      <div className="print:hidden bg-[#2C1810] text-white py-6 px-6">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-[#F5F1E6] hover:text-[#C17C3A] transition-colors">
            <ArrowLeft size={20} />
            <span className="font-manrope">Back to Home</span>
          </Link>
          <h1 className="font-playfair text-xl font-bold">Preorder Template</h1>
          <div className="flex gap-6">
            <button
              onClick={handlePrint}
              className="flex items-center gap-2 bg-[#C17C3A] text-white px-5 py-2 rounded-lg hover:bg-[#A0652D] transition-colors"
            >
              <Printer size={18} />
              <span className="hidden sm:inline">Print</span>
            </button>
            <button
              onClick={handleDownloadPDF}
              className="flex items-center gap-2 bg-[#F5F1E6] text-[#2C1810] px-5 py-2 rounded-lg hover:bg-white transition-colors"
            >
              <Download size={18} />
              <span className="hidden sm:inline">PDF</span>
            </button>
          </div>
        </div>
      </div>

      {/* Instructions - Hidden when printing */}
      <div className="print:hidden max-w-4xl mx-auto px-6 py-4 space-y-4">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-blue-800 text-sm">
            <strong>To book a table:</strong> Please call the pub directly. Phone bookings only.
          </p>
        </div>
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <p className="text-amber-800 text-sm">
            <strong>Preorders:</strong> Fill in this form and email it to the pub at least 48 hours before your booking. Print it, save as PDF, or download and complete digitally.
          </p>
        </div>
      </div>

      {/* Printable Template */}
      <div ref={printRef} className="max-w-4xl mx-auto px-6 py-8 print:px-0 print:py-0 print:max-w-none">
        <div className="bg-white rounded-lg shadow-lg p-8 print:shadow-none print:rounded-none">
          {/* Header */}
          <div className="text-center mb-8 print:mb-6">
            <h1 className="font-playfair text-3xl font-bold text-[#2C1810] print:text-2xl">The Three Horseshoes</h1>
            <p className="font-manrope text-[#C17C3A] text-lg mt-1">Food Preorder Form</p>
          </div>

          {/* Booking Info Section */}
          <div className="grid grid-cols-2 gap-4 mb-8 print:mb-6">
            <div className="flex items-center gap-2">
              <label className="font-manrope font-semibold text-[#2C1810] w-32">Booking Name:</label>
              <div className="flex-1 border-b-2 border-[#2C1810] h-8"></div>
            </div>
            <div className="flex items-center gap-2">
              <label className="font-manrope font-semibold text-[#2C1810] w-32">Contact Number:</label>
              <div className="flex-1 border-b-2 border-[#2C1810] h-8"></div>
            </div>
            <div className="flex items-center gap-2">
              <label className="font-manrope font-semibold text-[#2C1810] w-32">Date:</label>
              <div className="flex-1 border-b-2 border-[#2C1810] h-8"></div>
            </div>
            <div className="flex items-center gap-2">
              <label className="font-manrope font-semibold text-[#2C1810] w-32">Time:</label>
              <div className="flex-1 border-b-2 border-[#2C1810] h-8"></div>
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="bg-[#2C1810] text-white">
                  <th className="border border-[#2C1810] px-2 py-2 w-8 text-center">#</th>
                  <th className="border border-[#2C1810] px-2 py-2 text-left">Guest Name</th>
                  <th className="border border-[#2C1810] px-2 py-2 text-left">Starter</th>
                  <th className="border border-[#2C1810] px-2 py-2 text-left">Main</th>
                  <th className="border border-[#2C1810] px-2 py-2 text-left">Dessert</th>
                  <th className="border border-[#2C1810] px-2 py-2 text-left">Side Dishes</th>
                  <th className="border border-[#2C1810] px-2 py-2 text-left">Special Requirements</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((num) => (
                  <tr key={num} className="hover:bg-[#F5F1E6]/50 print:hover:bg-transparent">
                    <td className="border border-gray-300 px-2 py-3 text-center font-medium text-[#2C1810]">{num}</td>
                    <td className="border border-gray-300 px-2 py-3"></td>
                    <td className="border border-gray-300 px-2 py-3"></td>
                    <td className="border border-gray-300 px-2 py-3"></td>
                    <td className="border border-gray-300 px-2 py-3"></td>
                    <td className="border border-gray-300 px-2 py-3"></td>
                    <td className="border border-gray-300 px-2 py-3"></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Footer Note */}
          <div className="mt-6 pt-4 border-t border-gray-200 print:mt-4">
            <p className="text-xs text-gray-500 text-center">
              Please email this completed form to The Three Horseshoes at least 48 hours before your booking.<br />
              To book a table, please call us directly.<br />
              High St, Pentwyn-mawr, Newbridge, Newport NP11 4HN
            </p>
          </div>
        </div>
      </div>

      {/* Credits - Hidden when printing */}
      <div className="print:hidden py-8 px-6 bg-[#1a1a1a]">
        <div className="max-w-4xl mx-auto text-center">
          <a 
            href="mailto:builtbyjac@gmail.com"
            className="inline-block group"
          >
            <div className="text-gray-400 tracking-[0.3em] text-sm font-light uppercase hover:text-white transition-colors">
              BUILT BY JAC
            </div>
          </a>
          <div className="flex items-center justify-center gap-4 mt-3 text-xs">
            <a href="mailto:builtbyjac@gmail.com" className="text-gray-400 hover:text-white transition-colors">
              builtbyjac@gmail.com
            </a>
            <span className="text-gray-600">•</span>
            <a href="https://instagram.com/jacnewman08" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors">
              @jacnewman08
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PreorderTemplate;
